import { canonicalStockyPublicBigIntId, type StockyPublicBigIntId } from "./stocky_public_ids";

/** Shopify Admin Intent helpers used by the embedded App Home. */

export type ShopifyProductCreateIntentOutcome = "created" | "closed";

export class ShopifyProductCreateIntentError extends Error {
  constructor(
    public readonly code: "unavailable" | "error" | "invalid_response",
    message: string,
  ) {
    super(message);
    this.name = "ShopifyProductCreateIntentError";
  }
}

export type ShopifyProductEditIntentOutcome = "saved" | "closed";

export class ShopifyProductEditIntentError extends Error {
  constructor(
    public readonly code: "unavailable" | "error" | "invalid_response",
    message: string,
  ) {
    super(message);
    this.name = "ShopifyProductEditIntentError";
  }
}

/**
 * Open Shopify's native editor for the exact new product already created and
 * bound by the server-side replacement claim.
 *
 * Only the server-verified target product id is accepted. No source product
 * id, title, SKU, component, or Admin Intent response data is forwarded or
 * trusted here.
 */
export async function editShopifyProductWithAdminIntent(
  targetShopifyProductId: StockyPublicBigIntId,
): Promise<ShopifyProductEditIntentOutcome> {
  const intents = typeof window === "undefined" ? undefined : window.shopify?.intents;
  if (typeof intents?.invoke !== "function") {
    throw new ShopifyProductEditIntentError(
      "unavailable",
      "Shopify product editing is unavailable",
    );
  }
  const productId = canonicalStockyPublicBigIntId(targetShopifyProductId);
  const value = `gid://shopify/Product/${productId}`;

  let activity: Awaited<ReturnType<typeof intents.invoke>>;
  try {
    activity = await intents.invoke("edit:shopify/Product", { value });
  } catch (error) {
    throw new ShopifyProductEditIntentError(
      "error",
      error instanceof Error ? error.message : "Shopify product editing failed",
    );
  }
  if (
    activity === null ||
    typeof activity !== "object" ||
    typeof activity.complete?.then !== "function"
  ) {
    throw new ShopifyProductEditIntentError(
      "invalid_response",
      "Shopify product editing returned an invalid activity",
    );
  }

  let response: Awaited<typeof activity.complete>;
  try {
    response = await activity.complete;
  } catch (error) {
    throw new ShopifyProductEditIntentError(
      "error",
      error instanceof Error ? error.message : "Shopify product editing failed",
    );
  }
  if (response?.code === "ok") return "saved";
  if (response?.code === "closed") return "closed";
  if (response?.code === "error") {
    throw new ShopifyProductEditIntentError(
      "error",
      typeof response.message === "string" && response.message.trim() !== ""
        ? response.message
        : "Shopify product editing failed",
    );
  }
  throw new ShopifyProductEditIntentError(
    "invalid_response",
    "Shopify product editing returned an invalid response",
  );
}

/**
 * Open Shopify's native, merchant-controlled product creation screen.
 *
 * The create intent deliberately receives no value or data. In particular,
 * an old/deleted BOM ID, title, or SKU must never become input to Shopify.
 * The completion payload is only an acknowledgement: callers must re-sync
 * their own shop and require an explicit selection from the current catalog.
 */
export async function createShopifyProductWithAdminIntent(): Promise<ShopifyProductCreateIntentOutcome> {
  const intents = typeof window === "undefined" ? undefined : window.shopify?.intents;
  if (typeof intents?.invoke !== "function") {
    throw new ShopifyProductCreateIntentError(
      "unavailable",
      "Shopify product creation is unavailable",
    );
  }

  let activity: Awaited<ReturnType<typeof intents.invoke>>;
  try {
    // 🔴 Create means no old resource ID and no prefilled source data.
    activity = await intents.invoke("create:shopify/Product");
  } catch (error) {
    throw new ShopifyProductCreateIntentError(
      "error",
      error instanceof Error ? error.message : "Shopify product creation failed",
    );
  }
  if (
    activity === null ||
    typeof activity !== "object" ||
    typeof activity.complete?.then !== "function"
  ) {
    throw new ShopifyProductCreateIntentError(
      "invalid_response",
      "Shopify product creation returned an invalid activity",
    );
  }

  let response: Awaited<typeof activity.complete>;
  try {
    response = await activity.complete;
  } catch (error) {
    throw new ShopifyProductCreateIntentError(
      "error",
      error instanceof Error ? error.message : "Shopify product creation failed",
    );
  }
  if (response?.code === "ok") return "created";
  if (response?.code === "closed") return "closed";
  if (response?.code === "error") {
    throw new ShopifyProductCreateIntentError(
      "error",
      typeof response.message === "string" && response.message.trim() !== ""
        ? response.message
        : "Shopify product creation failed",
    );
  }
  throw new ShopifyProductCreateIntentError(
    "invalid_response",
    "Shopify product creation returned an invalid response",
  );
}
